/* eslint-disable react-native/no-unused-styles */
import React, { useState, useEffect, useMemo } from "react";
import { View, StyleSheet, Pressable, Animated, Dimensions } from "react-native";
import ChatMenuContainer from "../../chatmenu/ChatMenuContainer/ChatMenuContainer";
import ChatContainer from "../../chat/ChatContainer/ChatContainer";
import ChatTitle from "../../../components/chatWindow/ChatTitle/ChatTitle";
import { useChatBar, ConversationType } from "../../../contexts/ChatBarContext";
import { Ionicons } from "@expo/vector-icons";
import ChatBarContainer from "../../navigation/ChatBarContainer/ChatBarContainer";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { ChatMenu } from "../../../components/svgs/common";
import { useTodayChatQuery, useGetChatByObjectIdQuery, useGetChatsQuery } from "@axio/graphql/generated/graphql";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { Context } from "../../../contexts/ChatBarContext";
import useWebSocket from "../../../hooks/useWebSocket";
import { Platform } from "react-native";
import UpgradeModalContainer from "../../general/UpgradeModalContainer/UpgradeModalContainer";
import { useAppConfig } from "../../../AppConfigProvider";
import { MotiView } from "moti";
import CourseHeaderContainer from "../../customCoursework/CourseHeaderContainer";
import { getValidAccessToken } from "../../../utils/authRefresh";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

const height = Dimensions.get("window").height;

const ChatWindowContainer = () => {
  const { state: appState } = useAppState();

  const {
    showChat,
    setContext,
    state: chatBarState,
    setActiveContextAndObjectId,
    setConversationType,
    setSchoolMessage,
    slideAnim,
    handleMenuToggle,
    handleChatWindowOpenState,
  } = useChatBar();

  const { isMedium } = useResponsiveScreen();
  const { sendMessage } = useWebSocket();
  const { dispatch } = useAppState();
  const appConfig = useAppConfig();
  const isAxioOffering = appConfig.isAxioOffering;

  const [lastActiveChat, setLastActiveChat] = useState(null);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);

  const { data: chatsData, refetch: refetchChatsData } = useGetChatsQuery();

  // Query for today's chat with skip option
  const {
    data: todayChatData,
    loading: todayChatLoading,
    error: todayChatError,
    refetch: todayChatRefetch,
  } = useTodayChatQuery({
    variables: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    fetchPolicy: "network-only",
  });

  // Query for chat by object ID with skip option
  const {
    data: chatByObjectIdData,
    loading: chatByObjectIdLoading,
    error: chatByObjectIdError,
    refetch: chatByObjectIdRefetch,
  } = useGetChatByObjectIdQuery({
    variables: {
      objectId: chatBarState.activeObjectId,
      context: chatBarState.activeContext,
    },
    skip: !chatBarState.activeObjectId,
    fetchPolicy: "network-only",
  });

  const selectedChat = useMemo(() => {
    if (chatBarState.activeContext === Context.GENERAL) {
      return todayChatData?.getTodayChat;
    }
    return chatByObjectIdData?.getChatByObjectId;
  }, [chatBarState.activeContext, todayChatData, chatByObjectIdData]);

  useEffect(() => {
    if (appState.meta.isAtDayLimit || appState.meta.isAtLifetimeLimit) {
      setUpgradeModalVisible(true);
    }
  }, [appState.meta.isAtDayLimit, appState.meta.isAtLifetimeLimit]);

  useEffect(() => {
    refetchChatsData();

    if (selectedChat?.context && selectedChat.context !== "general") {
      setLastActiveChat(selectedChat);
    }
  }, [selectedChat]);

  useEffect(() => {
    if (chatBarState.activeObjectId) {
      chatByObjectIdRefetch({
        objectId: chatBarState.activeObjectId,
        context: chatBarState.activeContext,
      });
    } else {
      todayChatRefetch({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    }
  }, [appState.meta.chatHistory]);

  const handleModalClose = () => {
    setUpgradeModalVisible(false);
  };

  const onHideChat = () => handleChatWindowOpenState(false);

  const { trapRef: chatWindowRef } = useFocusTrap({
    visible: showChat,
    onEscape: onHideChat,
  });

  const handleGeneralChatTitlePress = async () => {
    dispatch({
      type: actions.SET_CONTENT,
      payload: { message: "" },
    });

    todayChatRefetch({
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    setLastActiveChat(chatByObjectIdData?.getChatByObjectId);

    setConversationType(ConversationType.GENERAL_CONVERSATION);

    setActiveContextAndObjectId({
      context: Context.GENERAL,
      objectId: "",
    });

    setContext({
      context: Context.GENERAL,
      params: null,
    });

    const token = await getValidAccessToken();
    sendMessage({
      meta: {
        token: token,
        abort: true,
      },
    });
  };

  const handleChatTitlePress = async () => {
    dispatch({
      type: actions.SET_CONTENT,
      payload: { message: "" },
    });

    chatByObjectIdRefetch({
      objectId: lastActiveChat.objectId,
      context: lastActiveChat.context,
    });

    setConversationType(
      lastActiveChat.context === Context.ASSIGNMENT || lastActiveChat.context === Context.AITUTOR
        ? ConversationType.SCHOOL
        : ConversationType.GENERAL_CONVERSATION
    );

    if (lastActiveChat.context === Context.ASSIGNMENT || lastActiveChat.context === Context.AITUTOR) {
      const { resource_id, course_id, integration_id, resource_type } = lastActiveChat.params;

      setSchoolMessage({
        contentType: resource_type,
        contentId: resource_id,
        classId: course_id,
        integrationId: integration_id,
      });
    }

    setActiveContextAndObjectId({
      context: lastActiveChat.context,
      objectId: lastActiveChat.objectId,
    });

    setContext({
      context: lastActiveChat.context as Context,
      params: lastActiveChat.params as { [key: string]: string },
    });

    const token = await getValidAccessToken();
    sendMessage({
      meta: {
        token: token,
        abort: true,
      },
    });
  };

  const handleChatPress = async (chat) => {
    chatByObjectIdRefetch({
      objectId: chat.objectId,
      context: chat.context,
    });
  };

  if (!showChat) {
    return null;
  }

  return (
    <MotiView
      ref={chatWindowRef}
      from={{ translateY: height }}
      animate={{ translateY: 0 }}
      exit={{ translateY: -100 }}
      transition={{
        type: "timing",
        duration: 450,
      }}
      removeClippedSubviews={true}
      style={styles().chatWindowContainer}
      // @ts-expect-error - web-only ARIA/role props not in MotiView RN types

      role="dialog"
      aria-modal={true}
      aria-label="Chat window"
      accessibilityViewIsModal={showChat}>
      {isMedium ? (
        <>
          <View style={styles(isMedium).container}>
            <View style={styles(isMedium).menu}>
              <ChatMenuContainer
                chatsData={chatsData}
                onChatPress={handleChatPress}
                onMobileChatPress={handleMenuToggle}
              />
            </View>

            <View style={styles(isMedium).chatContent}>
              <View style={styles(isMedium).mediumTopRow}>
                <Pressable
                  role="button"
                  aria-label="Close chat"
                  accessibilityRole="button"
                  accessibilityLabel="Close chat"
                  style={styles(isMedium).close}
                  onPress={onHideChat}>
                  <Ionicons name="close" size={24} color="black" />
                </Pressable>

                <View style={styles(isMedium).chatTitleRow}>
                  {lastActiveChat && (
                    <ChatTitle
                      title={lastActiveChat?.name}
                      active={chatBarState.activeContext !== Context.GENERAL}
                      onChatPress={handleChatTitlePress}
                    />
                  )}
                  {!isAxioOffering ? (
                    <ChatTitle
                      title={isAxioOffering ? "AI Assistant" : "General Chat"}
                      active={chatBarState.activeContext === Context.GENERAL}
                      onChatPress={handleGeneralChatTitlePress}
                    />
                  ) : (
                    <></>
                  )}
                </View>
              </View>
              <ChatContainer chatData={selectedChat} />
              <View style={styles(isMedium).bar}>
                <ChatBarContainer />
              </View>
            </View>
          </View>
        </>
      ) : (
        <>
          <View style={styles().mobile}>
            <Animated.View style={[styles().mobileContainer, { transform: [{ translateX: slideAnim }] }]}>
              {chatBarState.activeContext === Context.AITUTOR ? (
                <CourseHeaderContainer />
              ) : (
                <View style={styles().topRow}>
                  <Pressable
                    role="button"
                    aria-label="Toggle chat menu"
                    accessibilityRole="button"
                    accessibilityLabel="Toggle chat menu"
                    style={styles().menuButton}
                    onPress={handleMenuToggle}>
                    <ChatMenu />
                  </Pressable>

                  {lastActiveChat && (
                    <ChatTitle
                      title={lastActiveChat?.name}
                      active={chatBarState.activeContext !== Context.GENERAL}
                      onChatPress={handleChatTitlePress}
                    />
                  )}
                  {!isAxioOffering ? (
                    <ChatTitle
                      title={isAxioOffering ? "AI Assistant" : "General Chat"}
                      active={chatBarState.activeContext === Context.GENERAL}
                      onChatPress={handleGeneralChatTitlePress}
                    />
                  ) : (
                    <></>
                  )}

                  <Pressable
                    role="button"
                    aria-label="Close chat"
                    accessibilityRole="button"
                    accessibilityLabel="Close chat"
                    style={styles().closeButton}
                    onPress={onHideChat}>
                    <Ionicons name="close" size={24} color="black" />
                  </Pressable>
                </View>
              )}

              <ChatContainer chatData={selectedChat} />
            </Animated.View>

            <View style={styles().bar}>
              <ChatBarContainer />
            </View>

            {/* ChatMenuContainer that will slide in */}
            <Animated.View style={[styles().chatMenuContainer, { transform: [{ translateX: slideAnim }] }]}>
              <ChatMenuContainer
                chatsData={chatsData}
                onChatPress={handleChatPress}
                onMobileChatPress={handleMenuToggle}
              />
            </Animated.View>
          </View>
        </>
      )}
      <UpgradeModalContainer visible={upgradeModalVisible} onClose={handleModalClose} />
    </MotiView>
  );
};

const styles = (isMedium?: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: isMedium ? "rgba(255, 255, 255, 0.95)" : "#ffffff", // Solid color on mobile, transparent on web
      ...(Platform.OS === "web" &&
        isMedium && {
          backdropFilter: "blur(10px)", // Blur effect only on web when isMedium is true
        }),
    },
    chatWindowContainer: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 900,
      display: "flex",
      // flexDirection: "row",
      backgroundColor: "transparent",
    },
    mobileContainer: {
      flex: 1,
      backgroundColor: isMedium ? "rgba(255, 255, 255, 0.95)" : "#ffffff", // Adjust based on isMedium
    },
    chatMenuContainer: {
      position: "absolute",
      top: 0,
      left: -270,
      bottom: 56,
      zIndex: 100,
    },
    chatContent: {
      flex: 1,
      paddingTop: 20,
      paddingHorizontal: 20,
    },
    close: {
      alignSelf: "flex-end",
    },
    bar: {
      width: "100%",
      maxWidth: 1400,
      alignSelf: "center",
      zIndex: 2001,
      bottom: 0,
      left: 0,
      right: 0,
    },
    mediumTopRow: {
      flexDirection: "column",
      alignItems: "center",
    },
    topRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingHorizontal: 15,
      paddingVertical: 10,
      elevation: 2, // Adds a subtle shadow
    },
    chatTitleRow: {
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
    menuButton: {
      justifyContent: "flex-start",
    },
    closeButton: {
      height: 24,
      width: 24,
      justifyContent: "flex-end",
      alignItems: "flex-end",
    },
    mobile: {
      flex: 1,
    },
    menu: {
      zIndex: 2000,
      paddingVertical: 20,
      paddingLeft: 20,
    },
  });

export default ChatWindowContainer;
